import React, { useEffect, useMemo, useRef, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import { Check, Loader2, Printer, RefreshCw, Ticket, X } from "lucide-react";
import axios from "axios";
import { base_url } from "../../config";
import {
  darkBodyCellSx,
  darkHeadCellSx,
  darkMrtTheme,
  darkPaperSx,
  darkPinnedBodyCellSx,
  darkPinnedHeadCellSx,
  darkRowSx,
  darkSearchSx,
  darkToolbarSx,
} from "../Codes/darkTable";

type Row = {
  id: number;
  event: string;
  label: string;
  seat_index: number;
  seats_total: number;
  uuid: string;
  printed: boolean;
  printed_at: string | null;
  used_at: string | null;
  code: string;
  name: string;
  name_badge: string | null;
  affiliation_badge: string | null;
  type: string;
  cancellation_requested: number;
  email: string;
  diet: string | null;
};

// Mirrors the accent colours in backend/config/social_events.php, so a row in the console
// and the band on the printed ticket read as the same thing.
const EVENT_CHIP: Record<string, string> = {
  reception: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  student_reception: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  gala_dinner: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  diversity_inclusion: "bg-violet-500/15 text-violet-300 border-violet-500/30",
};

const chipClass = (event: string) =>
  EVENT_CHIP[event] || "bg-slate-500/15 text-slate-300 border-slate-500/30";


/** Save a PDF response under `filename`. */
const saveBlob = (data: BlobPart, filename: string) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const SocialEvents = () => {
  const [data, setData] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState("all");
  const [unprintedOnly, setUnprintedOnly] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  const [notice, setNotice] = useState("");
  // Per row, so printing one ticket does not lock the others.
  const [busyIds, setBusyIds] = useState<number[]>([]);
  const busy = useRef<Set<number>>(new Set());

  const load = async () => {
    try {
      const response = await axios.get(`${base_url}/api/admin/social_event_tickets`);
      setData(response.data || []);
    } catch (error) {
      console.error("Error fetching social event tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const events = useMemo(() => {
    const counts = new Map<string, { label: string; total: number; unprinted: number }>();
    data.forEach((row) => {
      const entry = counts.get(row.event) || { label: row.label, total: 0, unprinted: 0 };
      entry.total += 1;
      if (!row.printed) entry.unprinted += 1;
      counts.set(row.event, entry);
    });
    return Array.from(counts.entries());
  }, [data]);

  const filtered = useMemo(
    () =>
      data.filter(
        (row) =>
          (eventFilter === "all" || row.event === eventFilter) &&
          (!unprintedOnly || !row.printed)
      ),
    [data, eventFilter, unprintedOnly]
  );

  const unprintedInScope = filtered.filter((row) => !row.printed).length;

  const withRowBusy = async (id: number, run: () => Promise<void>) => {
    if (busy.current.has(id)) return;
    busy.current.add(id);
    setBusyIds((prev) => [...prev, id]);
    try {
      await run();
    } finally {
      busy.current.delete(id);
      setBusyIds((prev) => prev.filter((busyId) => busyId !== id));
    }
  };

  const handlePrint = (row: Row) =>
    withRowBusy(row.id, async () => {
      try {
        const response = await axios.post(
          `${base_url}/api/admin/print_social_event_ticket`,
          { id: row.id },
          { responseType: "blob" }
        );
        saveBlob(response.data, `ticket_${row.code}_${row.event}_${row.seat_index + 1}.pdf`);
      } catch (error) {
        console.error("Error printing ticket:", error);
      }
    });

  const handleTogglePrinted = (row: Row) =>
    withRowBusy(row.id, async () => {
      try {
        const response = await axios.post(
          `${base_url}/api/admin/update_social_event_ticket_printed`,
          { id: row.id, value: !row.printed }
        );
        const printed = response.data.printed;
        setData((prev) =>
          prev.map((item) =>
            item.id === row.id
              ? { ...item, printed, printed_at: printed ? new Date().toISOString() : null }
              : item
          )
        );
      } catch (error) {
        console.error("Error updating printed flag:", error);
      }
    });

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setNotice("");
    try {
      const response = await axios.post(`${base_url}/api/admin/sync_social_event_tickets`);
      const { created, deleted, stranded, total } = response.data;
      const parts = [`${created} created`, `${deleted} removed`, `${total} on file`];
      if (stranded?.length) {
        parts.push(`${stranded.length} seat(s) given up after printing — left in place`);
      }
      setNotice(parts.join(" · "));
      await load();
    } catch (error) {
      console.error("Error generating tickets:", error);
      setNotice("Could not generate tickets — see the console.");
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Print everything unprinted in the current filter. The server bounds each response
   * and flags what it emitted, so asking again picks up where this left off; loop until
   * it reports nothing remaining.
   */
  const handleBulkPrint = async () => {
    if (syncing) return;
    setSyncing(true);
    setNotice("");
    let part = 0;
    let printed = 0;
    try {
      for (;;) {
        part += 1;
        setBulkStatus(`Building part ${part}…`);
        const response = await axios.post(
          `${base_url}/api/admin/print_social_event_tickets`,
          {
            event: eventFilter === "all" ? null : eventFilter,
            only_unprinted: true,
          },
          { responseType: "blob" }
        );
        // An empty batch comes back as JSON rather than a PDF.
        if (response.data.type === "application/json") {
          break;
        }
        const count = Number(response.headers["x-printed"] || 0);
        const remaining = Number(response.headers["x-remaining"] || 0);
        printed += count;
        saveBlob(response.data, `social_event_tickets_${String(part).padStart(2, "0")}.pdf`);
        if (!remaining || !count) break;
      }
      setNotice(`${printed} ticket(s) printed in ${part} file(s) and marked as printed.`);
      await load();
    } catch (error) {
      console.error("Error printing tickets:", error);
      setNotice("Printing stopped early — see the console.");
    } finally {
      setBulkStatus("");
      setSyncing(false);
    }
  };

  const columns = [
    {
      accessorKey: "label",
      header: "Event",
      size: 170,
      Cell: ({ row }: { row: { original: Row } }) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${chipClass(
            row.original.event
          )}`}
        >
          {row.original.label}
        </span>
      ),
    },
    {
      accessorKey: "code",
      header: "Code",
      size: 100,
      Cell: ({ row }: { row: { original: Row } }) => (
        <span className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-100 border border-slate-600/40">
          {row.original.code}
        </span>
      ),
    },
    {
      accessorKey: "name",
      header: "Delegate",
      size: 220,
      Cell: ({ row }: { row: { original: Row } }) => (
        <div className="min-w-0">
          <div className="truncate">{row.original.name_badge || row.original.name}</div>
          <div className="text-[11px] text-slate-500 truncate">{row.original.email}</div>
        </div>
      ),
    },
    {
      accessorKey: "affiliation_badge",
      header: "Affiliation",
      size: 180,
      Cell: ({ row }: { row: { original: Row } }) => (
        <span className="text-slate-300 text-xs">{row.original.affiliation_badge || "—"}</span>
      ),
    },
    {
      id: "seat",
      header: "Guest",
      size: 90,
      accessorFn: (row: Row) => `${row.seat_index + 1}/${row.seats_total}`,
      Cell: ({ row }: { row: { original: Row } }) => (
        <span className="text-xs text-amber-300">
          {row.original.seats_total > 1
            ? `Guest ${row.original.seat_index + 1} of ${row.original.seats_total}`
            : "Guest"}
        </span>
      ),
    },
    {
      accessorKey: "diet",
      header: "Diet",
      size: 90,
      Cell: ({ row }: { row: { original: Row } }) =>
        row.original.diet ? (
          <span className="text-xs text-emerald-300">{row.original.diet}</span>
        ) : (
          <span className="text-xs text-slate-600">—</span>
        ),
    },
    {
      accessorKey: "printed",
      header: "Printed",
      size: 100,
      Cell: ({ row }: { row: { original: Row } }) =>
        row.original.printed ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
            <Check className="h-3.5 w-3.5" />
            Yes
          </span>
        ) : (
          <span className="text-xs text-slate-500">No</span>
        ),
    },
    {
      accessorKey: "used_at",
      header: "Used",
      size: 130,
      Cell: ({ row }: { row: { original: Row } }) =>
        row.original.used_at ? (
          <span className="text-[11px] text-violet-300">{row.original.used_at}</span>
        ) : (
          <span className="text-xs text-slate-600">—</span>
        ),
    },
    {
      id: "actions",
      header: "Print",
      enableSorting: false,
      enableColumnActions: false,
      enableHiding: false,
      size: 110,
      // Pinned right: the columns before it are wider than most screens, and an action
      // column that scrolls off the edge reads as a missing feature. The pinned styles
      // only make it opaque — it should look like any other column.
      muiTableHeadCellProps: { sx: { ...darkHeadCellSx, ...darkPinnedHeadCellSx } },
      muiTableBodyCellProps: { sx: { ...darkBodyCellSx, ...darkPinnedBodyCellSx } },
      Cell: ({ row }: { row: { original: Row } }) => {
        const isBusy = busyIds.includes(row.original.id);
        return (
          <div className="flex items-center gap-1.5 justify-end">
            <button
              onClick={() => handlePrint(row.original)}
              disabled={isBusy}
              title="Print this entrance"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 hover:text-blue-200 transition-colors disabled:opacity-50"
            >
              {isBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Printer className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              onClick={() => handleTogglePrinted(row.original)}
              disabled={isBusy}
              title={row.original.printed ? "Mark as not printed" : "Mark as printed"}
              className={`inline-flex items-center justify-center h-7 w-7 rounded-md border transition-colors disabled:opacity-50 ${
                row.original.printed
                  ? "bg-slate-600/30 text-slate-300 border-slate-600/40 hover:bg-slate-600/50"
                  : "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25"
              }`}
            >
              {row.original.printed ? <X className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
            </button>
          </div>
        );
      },
    },
  ];

  const chip = (active: boolean) =>
    `inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
      active
        ? "bg-blue-500/25 text-blue-100 border-blue-400/50"
        : "bg-slate-800/60 text-slate-300 border-slate-700/50 hover:border-slate-600/70"
    }`;

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-[1500px] px-4 py-5 space-y-4">
        <header className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Ticket className="h-5 w-5 text-blue-400" />
            Social event entrances
            <span className="text-xs font-normal text-slate-400">
              ({filtered.length} of {data.length})
            </span>
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (
                  window.confirm(
                    "This rebuilds the guest entrances from what delegates are entitled to. " +
                      "Seats that are no longer held are deleted, unless their ticket has " +
                      "already been printed or scanned — those are reported instead. Continue?"
                  )
                ) {
                  handleSync();
                }
              }}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700/60 transition-colors disabled:opacity-50"
            >
              {syncing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Generate missing tickets
            </button>
            <button
              onClick={() => {
                if (
                  window.confirm(
                    `This builds a PDF of the ${unprintedInScope} unprinted entrance(s) currently listed, three to an A4 sheet, and marks them as printed. Large batches arrive as several files. Continue?`
                  )
                ) {
                  handleBulkPrint();
                }
              }}
              disabled={syncing || unprintedInScope === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 transition-colors disabled:opacity-50"
            >
              <Printer className="h-3.5 w-3.5" />
              {bulkStatus || `Print all unprinted (${unprintedInScope})`}
            </button>
          </div>
        </header>

        {/* Without this the row count reads as a bug: a few dozen entrances for a few
            thousand delegates is the whole point of the policy. */}
        <div className="rounded-lg border border-blue-500/25 bg-blue-500/10 px-3 py-2 text-xs text-blue-100/90">
          These are <strong>guest</strong> entrances only. Delegates get in on their own
          conference badge at every event they hold a seat at — included with their
          registration type or bought — so a delegate attending alone has no row here. Only
          the seats they bought on top of their own are printed.
        </div>

        {notice && (
          <div className="rounded-lg border border-slate-700/40 bg-slate-800/40 px-3 py-2 text-xs text-slate-300">
            {notice}
          </div>
        )}

        <div className="flex items-center gap-1.5 flex-wrap">
          <button className={chip(eventFilter === "all")} onClick={() => setEventFilter("all")}>
            All events
            <span className="text-slate-400">{data.length}</span>
          </button>
          {events.map(([key, info]) => (
            <button key={key} className={chip(eventFilter === key)} onClick={() => setEventFilter(key)}>
              {info.label}
              <span className="text-slate-400">
                {info.unprinted}/{info.total}
              </span>
            </button>
          ))}
          <span className="mx-1 h-4 w-px bg-slate-700/60" />
          <button className={chip(unprintedOnly)} onClick={() => setUnprintedOnly(!unprintedOnly)}>
            Unprinted only
          </button>
        </div>

        <div className="rounded-2xl overflow-hidden">
          <MaterialReactTable
            columns={columns}
            data={filtered}
            state={{ isLoading: loading }}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            enableColumnPinning
            mrtTheme={darkMrtTheme}
            initialState={{
              density: "compact",
              pagination: { pageSize: 25, pageIndex: 0 },
              columnPinning: { right: ["actions"] },
            }}
            muiTablePaperProps={{ sx: darkPaperSx }}
            muiTableContainerProps={{ sx: { backgroundColor: "transparent" } }}
            muiTopToolbarProps={{ sx: darkToolbarSx }}
            muiBottomToolbarProps={{ sx: darkToolbarSx }}
            muiTableHeadCellProps={{ sx: darkHeadCellSx }}
            muiTableBodyCellProps={{ sx: darkBodyCellSx }}
            muiTableBodyRowProps={{ sx: darkRowSx }}
            muiSearchTextFieldProps={{ placeholder: "Search…", sx: darkSearchSx }}
          />
        </div>
      </div>
    </div>
  );
};

export default SocialEvents;
